import { useState, useEffect, useCallback } from 'react'
import { Activity, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import { getSystemHealth } from '../services/systemHealthService.js'

const SystemHealthPage = () => {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastChecked, setLastChecked] = useState(null)

  const fetchHealth = useCallback(() => {
    setLoading(true)
    setError(null)
    getSystemHealth()
      .then((res) => {
        setServices(res.data)
        setLastChecked(new Date())
      })
      .catch(() => setError('Failed to load health status'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchHealth()
    const interval = setInterval(fetchHealth, 30000)
    return () => clearInterval(interval)
  }, [fetchHealth])

  const upCount = services.filter((s) => s.status === 'UP').length
  const downCount = services.filter((s) => s.status !== 'UP').length

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" aria-hidden="true" />
            <span className="font-semibold">{upCount} Healthy</span>
          </div>
          {downCount > 0 && (
            <div className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" aria-hidden="true" />
              <span className="font-semibold">{downCount} Down</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastChecked && (
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <Clock className="w-4 h-4" aria-hidden="true" />
              Last checked: {lastChecked.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
            aria-label="Refresh health status"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchHealth} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Service Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {services.map((service) => {
          const isUp = service.status === 'UP'
          const responseColor = service.responseTimeMs > 1000 ? 'text-amber-400' : service.responseTimeMs > 500 ? 'text-yellow-400' : 'text-green-400'
          return (
            <div
              key={service.serviceName}
              className={`bg-white dark:bg-gray-800 rounded-2xl p-6 border ${isUp ? 'border-green-500/30' : 'border-red-500/30'}`}
            >
              <div className="flex items-center justify-between mb-4">
                <Activity className={`w-5 h-5 ${isUp ? 'text-green-400' : 'text-red-400'}`} aria-hidden="true" />
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${isUp ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                >
                  {service.status}
                </span>
              </div>
              <h3 className="font-semibold text-lg">{service.serviceName}</h3>
              <p className="text-xs text-gray-400 mt-1 truncate" title={service.url}>{service.url}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Response: <span className={`font-medium ${responseColor}`}>{service.responseTimeMs}ms</span>
              </p>
            </div>
          )
        })}
      </div>

      {services.length === 0 && !loading && !error && (
        <div className="text-center py-12 text-gray-400">No services configured for health monitoring</div>
      )}
    </div>
  )
}

export default SystemHealthPage
